import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ListType } from '@pacer/shared';
import * as api from '../api';
import { Icon, type IconName } from './Icon';
import { Modal } from './Modal';
import { QuickAdd } from './QuickAdd';
import { cx } from './ui';
import { EventEditor } from '../views/EventEditor';
import { useActiveMemberId } from '../state/activeMember';
import { queryKeys } from '../state/queries';

type Target = { kind: 'list'; type: ListType } | { kind: 'event' };

const ACTIONS: Array<{ label: string; hint: string; icon: IconName; target: Target }> = [
  { label: 'To-do', hint: 'Jot it down', icon: 'note', target: { kind: 'list', type: 'todo' } },
  { label: 'Shopping', hint: 'Add an item', icon: 'cart', target: { kind: 'list', type: 'shopping' } },
  { label: 'Event', hint: 'Put it on the calendar', icon: 'calendar', target: { kind: 'event' } },
];

/**
 * The phone's whole reason for existing: capture something in two taps while standing in
 * the kitchen. Big targets, no navigation, no scrolling to find the right field.
 */
export function QuickCapture() {
  const [open, setOpen] = useState<Target | null>(null);

  return (
    <>
      <div className="grid grid-cols-3 gap-2">
        {ACTIONS.map((a) => (
          <button
            key={a.label}
            onClick={() => setOpen(a.target)}
            className={cx(
              'group flex flex-col items-center justify-center gap-2 rounded-lg border border-border-subtle bg-surface px-2 py-5',
              'transition-all duration-150 hover:border-border hover:bg-surface-high/60 active:scale-[0.98]',
            )}
          >
            <Icon
              name={a.icon}
              size={22}
              className="text-muted transition-colors group-hover:text-accent"
            />
            <span className="text-[13px] font-medium text-primary">{a.label}</span>
          </button>
        ))}
      </div>

      {open?.kind === 'list' && <ListCapture type={open.type} onClose={() => setOpen(null)} />}
      {open?.kind === 'event' && <EventEditor onClose={() => setOpen(null)} />}
    </>
  );
}

function ListCapture({ type, onClose }: { type: ListType; onClose: () => void }) {
  const qc = useQueryClient();
  const activeId = useActiveMemberId();
  const [added, setAdded] = useState<string[]>([]);

  const { data: lists = [] } = useQuery({ queryKey: queryKeys.lists, queryFn: api.getLists });
  const list = lists.find((l) => l.type === type);

  const { data: suggestions = [] } = useQuery({
    queryKey: ['suggestions', list?.id],
    queryFn: () => api.getSuggestions(list!.id),
    enabled: Boolean(list),
  });

  const add = useMutation({
    mutationFn: (text: string) => api.addListItem(list!.id, text, { createdBy: activeId }),
    onSuccess: async (_d, text) => {
      // Stay open and show a running tally — capture is usually more than one thing.
      setAdded((a) => [text, ...a]);
      await qc.invalidateQueries({ queryKey: queryKeys.lists });
      await qc.invalidateQueries({ queryKey: ['suggestions', list?.id] });
    },
  });

  return (
    <Modal
      open
      onClose={onClose}
      eyebrow="Quick add"
      title={list?.name ?? (type === 'shopping' ? 'Shopping' : 'To-do')}
    >
      {!list ? (
        <p className="py-6 text-center font-mono text-[12px] text-muted">No list found.</p>
      ) : (
        <div className="flex flex-col gap-4">
          <QuickAdd
            placeholder={type === 'shopping' ? 'Add an item…' : 'Add a to-do…'}
            onAdd={(t) => add.mutate(t)}
            suggestions={suggestions.slice(0, 12)}
            busy={add.isPending}
          />

          {added.length > 0 && (
            <div className="flex flex-col gap-1.5 border-t border-border-subtle pt-3">
              <span className="eyebrow">Added</span>
              {added.map((t, i) => (
                <span key={`${t}-${i}`} className="flex items-center gap-2 text-[14px] text-secondary">
                  <Icon name="check" size={12} className="text-success" />
                  {t}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
